import { parseBoolean } from "@punchup/utils/env";
import { getHostname } from "@punchup/utils/hostname";
import { sanitizeStatsigEventName } from "@punchup/utils/sanitize-statsig-event-name";
import { getStoredShortLinkContext } from "@punchup/utils/shortlink";
import mixpanel, { RequestOptions } from "mixpanel-browser";

import Statsig from "statsig-js";
import { incrementSouthpawCounters } from "./utils";
import { clearStoredUserRoles, getStoredUserRoles } from "./user-roles";
import { logException } from "@punchup/sentry";

if (typeof window !== "undefined") {
  // parse the environment variable from a string to a boolean
  if (parseBoolean(process.env.DISABLE_ANALYTICS_TRACKING)) {
    console.log(
      "Analytics events won't be submitted to Mixpanel in development; you can change this by editing the DISABLE_ANALYTICS_TRACKING env var in next.config.js"
    );
  }

  if (process.env.MIXPANEL_TOKEN) {
    mixpanel.init(process.env.MIXPANEL_TOKEN, {
      // parse the environment variable from a string to a boolean
      debug: parseBoolean(process.env.IS_DEV), // disable debug mode in production
      // track_pageview: true,  // Commenting out as it throws a typing error, even though this is valid property https://docs.mixpanel.com/docs/tracking/reference/javascript#tracking-page-views
      persistence: "localStorage",
      ignore_dnt: true,
      // Please note that all Mixpanel requests are reverse proxied via our Next.js server.
      // This configuration is visible in next.config.js, in the rewrites section.
      // This parameter `api_host` tells the Mixpanel SDK where these requests should be forwarded to, which is normally punchup.live
      // since we typically do not want to submit Mixpanel requests from Dev or Preview. With that said, it is possible to override this
      // setting by flipping the DISABLE_ANALYTICS_TRACKING env variable.
      api_host: getHostname(),
    });
  } else {
    console.warn(
      "Mixpanel token not found. Analytics tracking will be disabled."
    );
  }
}

/*
 * This will track an event in Mixpanel and Statsig. The Statsig call is enabled by default
 * but can be overriden (i.e. not made) by setting the parameter logToStatsig = false.
 */
export const trackEvent = async (
  event: string,
  properties?: Record<string, any>,
  options?: RequestOptions,
  callback?: () => void,
  logToStatsig = true
) => {
  const shortLinkCtx = getStoredShortLinkContext();
  const { shortCode, southpawJobId, attribution } = shortLinkCtx || {};
  const mergedProps: Record<string, any> = {
    ...(properties || {}),
    ...(shortCode ? { short_code: shortCode } : {}),
    ...(southpawJobId ? { southpaw_job_id: southpawJobId } : {}),
    ...(attribution ?? {}),
    // Role super-properties, attached to every event and spread last so they
    // are always present as real booleans and cannot be clobbered by a caller.
    ...getStoredUserRoles(),
  };

  // parse the environment variable from a string to a boolean
  const analyticsDisabled = parseBoolean(
    process.env.DISABLE_ANALYTICS_TRACKING
  );

  if (analyticsDisabled) {
    console.log(
      "Mixpanel event: ",
      event,
      mergedProps ? "with properties:" : "",
      mergedProps ? mergedProps : "",
      logToStatsig === true
        ? "and this event is set to be LOGGED in Statsig"
        : "and this event is set to NOT BE LOGGED in Statsig"
    );

    // Log what WOULD happen in production (for developer awareness)
    // This will only log if the event is in ACTION_EVENT_MAP
    await incrementSouthpawCounters({
      event,
      shortCode,
      southpawJobId,
      devMode: true,
    });

    if (callback) {
      return callback();
    } else {
      return;
    }
  }
  try {
    mixpanel.track(event, mergedProps, options, callback);
  } catch (err) {
    console.error("Error tracking event, err: ", err);
    logException(`Error tracking event "${event}" to Mixpanel: ${err}`);
  }
  if (logToStatsig === true) {
    try {
      Statsig.logEvent(sanitizeStatsigEventName(event), 0, mergedProps);
    } catch (err) {
      console.error("Error logging event to Statsig, err: ", err);
      logException(`Error logging event "${event}" to Statsig: ${err}`);
    }
  }

  // Increment Postgres counters when applicable
  await incrementSouthpawCounters({ event, shortCode, southpawJobId });
};

// This is important: we call this once upon user log in,
//  i.e. as soon as we get their Supabase User Id from the session
// The goal is to tie all subsequent tracking calls to that user id and
// tie all previous (anonymous) calls to that user
export const identifyUser = (persistentUserId: string) => {
  // parse the environment variable from a string to a boolean
  if (parseBoolean(process.env.DISABLE_ANALYTICS_TRACKING)) {
    console.log("Identified User with persistent id:", persistentUserId);
    return;
  }
  try {
    // There are two ways to do this dependening on a certain Mixpanel Project setting called "ID Merge", which
    // Mixpanel introduced in June '23. Our project was created on May 31, 2023 so we're stuck
    // with the old way, unless we create a new project.
    //
    // NEW WAY: (ID Merge-enabled project)
    // mixpanel.identify(persistentUserId);
    //
    // OLD WAY: (Not ID Merge-enabled project)
    // 1. https://docs.mixpanel.com/docs/tracking/reference/javascript-full-api-reference#mixpanelalias
    const anonymousId = mixpanel.get_distinct_id();
    mixpanel.alias(persistentUserId, anonymousId);
    // 2. https://docs.mixpanel.com/docs/tracking/reference/javascript-full-api-reference#mixpanelidentify
    mixpanel.identify(persistentUserId);
  } catch (err) {
    // TODO add Logger
    console.error("Error while identifying user: ", err);
    logException(`Error while identifying user: ${err}`);
  }
};

// We call this once, upon logging out
// It tells Mixpanel to stop tracking events for the current user
export const resetAnalytics = () => {
  // parse the environment variable from a string to a boolean
  if (parseBoolean(process.env.DISABLE_ANALYTICS_TRACKING)) {
    console.log("Reset Mixpanel analytics");
    return;
  }
  try {
    // Drop the user-scoped role flags so a subsequent anonymous session
    // reports all-false rather than the previous user's roles.
    clearStoredUserRoles();
    mixpanel.reset();
  } catch (err) {
    // TODO add Logger
    console.error("Error while resetting Mixpanel analytics: ", err);
    logException(`Error while resetting Mixpanel analytics: ${err}`);
  }
};

// Get the current mixpanel distinct ID for tracking purposes
export const getMixpanelDistinctId = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return mixpanel.get_distinct_id();
  } catch (err) {
    console.error("Error getting mixpanel distinct ID: ", err);
    logException(`Error getting mixpanel distinct ID: ${err}`);
    return undefined;
  }
};
